// src/components/IssuerTab.js
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';

function IssuerTab() {
  const { authFetch } = useAuth();
  const [issuers, setIssuers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [newAlias, setNewAlias] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newIssuer, setNewIssuer] = useState({ nss: '', product_name: '', provider_name: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [unmapped, setUnmapped] = useState([]);
  const [showUnmapped, setShowUnmapped] = useState(false);

  const fetchIssuers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await authFetch('/api/issuers');
      const data = await response.json();
      setIssuers(data);
    } catch (error) {
      console.error('Gagal mengambil data issuer:', error);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  const fetchUnmapped = useCallback(async () => {
    try {
      const response = await authFetch('/api/issuers/unmapped');
      const data = await response.json();
      setUnmapped(data);
    } catch (error) {
      console.error('Gagal mengambil unmapped:', error);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchIssuers();
    fetchUnmapped();
  }, [fetchIssuers, fetchUnmapped]);

  const handleAddIssuer = async (e) => {
    e.preventDefault();
    try {
      const response = await authFetch('/api/issuers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newIssuer),
      });
      if (response.ok) {
        toast.success('Issuer berhasil ditambahkan');
        setNewIssuer({ nss: '', product_name: '', provider_name: '' });
        setShowAddForm(false);
        fetchIssuers();
      } else {
        const err = await response.json();
        toast.error(err.error || 'Gagal menambahkan issuer');
      }
    } catch (error) {
      toast.error('Gagal menambahkan issuer');
    }
  };

  const handleRemap = async () => {
    try {
      const response = await authFetch('/api/issuers/remap', { method: 'POST' });
      const data = await response.json();
      toast.success(data.message);
      fetchUnmapped();
    } catch (error) {
      toast.error('Gagal remap');
    }
  };

  const handleDeleteIssuer = async (id) => {
    if (!window.confirm('Hapus issuer ini beserta semua aliasnya?')) return;
    try {
      const response = await authFetch(`/api/issuers/${id}`, { method: 'DELETE' });
      if (response.ok) {
        toast.success('Issuer berhasil dihapus');
        fetchIssuers();
      } else {
        toast.error('Gagal menghapus issuer');
      }
    } catch (error) {
      toast.error('Gagal menghapus issuer');
    }
  };

  const handleAddAlias = async (issuerId) => {
    if (!newAlias.trim()) return;
    try {
      const response = await authFetch(`/api/issuers/${issuerId}/aliases`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ alias: newAlias.trim() }),
      });
      if (response.ok) {
        toast.success('Alias berhasil ditambahkan');
        setNewAlias('');
        fetchIssuers();
      } else {
        const err = await response.json();
        toast.error(err.error || 'Gagal menambahkan alias');
      }
    } catch (error) {
      toast.error('Gagal menambahkan alias');
    }
  };

  const handleDeleteAlias = async (aliasId) => {
    try {
      const response = await authFetch(`/api/issuers/aliases/${aliasId}`, { method: 'DELETE' });
      if (response.ok) {
        toast.success('Alias berhasil dihapus');
        fetchIssuers();
      } else {
        toast.error('Gagal menghapus alias');
      }
    } catch (error) {
      toast.error('Gagal menghapus alias');
    }
  };

  const filteredIssuers = issuers.filter(iss => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      iss.nss.toLowerCase().includes(term) ||
      iss.product_name.toLowerCase().includes(term) ||
      iss.provider_name.toLowerCase().includes(term) ||
      iss.aliases.some(a => a.toLowerCase().includes(term))
    );
  });

  return (
    <div>
      <div className="tab-header-actions">
        <h2>Issuer & Alias Management</h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={() => setShowUnmapped(!showUnmapped)} className="add-new-btn" style={{ background: unmapped.length > 0 ? '#dc3545' : '#28a745' }}>
            Unmapped ({unmapped.length})
          </button>
          <button onClick={() => setShowAddForm(!showAddForm)} className="add-new-btn">
            {showAddForm ? 'Cancel' : 'Add Issuer'}
          </button>
        </div>
      </div>

      {showUnmapped && unmapped.length > 0 && (
        <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '6px', padding: '12px', marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <strong>Unmapped Issuers (belum ter-mapping ke master data)</strong>
            <button onClick={handleRemap} className="add-new-btn" style={{ padding: '4px 12px', fontSize: '12px' }}>Re-map Now</button>
          </div>
          <p style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>Tambahkan alias yang sesuai di issuer terkait, lalu klik "Re-map Now".</p>
          <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #ddd' }}>
                <th style={{ textAlign: 'left', padding: '4px 8px' }}>Raw Payment Issuer</th>
                <th style={{ textAlign: 'right', padding: '4px 8px' }}>Total Count</th>
                <th style={{ textAlign: 'right', padding: '4px 8px' }}>Records</th>
              </tr>
            </thead>
            <tbody>
              {unmapped.map((item, idx) => (
                <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>{item.payment_issuer}</td>
                  <td style={{ textAlign: 'right', padding: '4px 8px' }}>{item.total_count.toLocaleString()}</td>
                  <td style={{ textAlign: 'right', padding: '4px 8px' }}>{item.record_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showUnmapped && unmapped.length === 0 && (
        <div style={{ background: '#d4edda', border: '1px solid #28a745', borderRadius: '6px', padding: '12px', marginBottom: '16px' }}>
          Semua data sudah ter-mapping!
        </div>
      )}

      {showAddForm && (
        <form onSubmit={handleAddIssuer} style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="NSS (e.g. 93600002)"
            value={newIssuer.nss}
            onChange={e => setNewIssuer({ ...newIssuer, nss: e.target.value })}
            required
            style={{ padding: '6px 10px' }}
          />
          <input
            type="text"
            placeholder="Nama Produk"
            value={newIssuer.product_name}
            onChange={e => setNewIssuer({ ...newIssuer, product_name: e.target.value })}
            required
            style={{ padding: '6px 10px' }}
          />
          <input
            type="text"
            placeholder="Nama Penyelenggara"
            value={newIssuer.provider_name}
            onChange={e => setNewIssuer({ ...newIssuer, provider_name: e.target.value })}
            required
            style={{ padding: '6px 10px', minWidth: '250px' }}
          />
          <button type="submit" className="add-new-btn">Save</button>
        </form>
      )}

      <input
        type="text"
        placeholder="Search issuer (NSS, nama produk, penyelenggara, alias)..."
        value={searchTerm}
        onChange={e => setSearchTerm(e.target.value)}
        style={{ padding: '8px 12px', width: '100%', maxWidth: '400px', marginBottom: '12px' }}
      />

      {loading ? (
        <p>Loading data...</p>
      ) : (
        <div className="table-responsive">
          <table className="master-data-table">
            <thead>
              <tr>
                <th>NSS</th>
                <th>Nama Produk</th>
                <th>Nama Penyelenggara</th>
                <th>Aliases</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredIssuers.map(iss => (
                <React.Fragment key={iss.id}>
                  <tr>
                    <td>{iss.nss}</td>
                    <td>{iss.product_name}</td>
                    <td>{iss.provider_name}</td>
                    <td>
                      <span
                        style={{ cursor: 'pointer', color: '#007bff' }}
                        onClick={() => setExpandedId(expandedId === iss.id ? null : iss.id)}
                      >
                        {iss.aliases.length} alias {expandedId === iss.id ? '▲' : '▼'}
                      </span>
                    </td>
                    <td className="actions-cell">
                      <button onClick={() => handleDeleteIssuer(iss.id)} className="delete-btn">Delete</button>
                    </td>
                  </tr>
                  {expandedId === iss.id && (
                    <tr>
                      <td colSpan="5" style={{ backgroundColor: '#f8f9fa', padding: '12px' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                          {iss.aliases.map((alias, idx) => (
                            <span key={idx} style={{
                              display: 'inline-flex', alignItems: 'center', gap: '4px',
                              background: '#e9ecef', padding: '4px 8px', borderRadius: '4px', fontSize: '13px'
                            }}>
                              {alias}
                              <button
                                onClick={() => {
                                  const aliasObj = issuers.find(i => i.id === iss.id);
                                  // Need alias id - fetch from backend
                                  authFetch(`/api/issuers/${iss.id}/aliases`).then(r => r.json()).then(aliases => {
                                    const found = aliases.find(a => a.alias === alias);
                                    if (found) handleDeleteAlias(found.id);
                                  });
                                }}
                                style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#dc3545', fontWeight: 'bold' }}
                              >×</button>
                            </span>
                          ))}
                        </div>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <input
                            type="text"
                            placeholder="Tambah alias baru..."
                            value={newAlias}
                            onChange={e => setNewAlias(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddAlias(iss.id); }}}
                            style={{ padding: '4px 8px', flex: 1, maxWidth: '300px' }}
                          />
                          <button onClick={() => handleAddAlias(iss.id)} className="add-new-btn" style={{ padding: '4px 12px' }}>+</button>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {filteredIssuers.length === 0 && (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'center', padding: '20px' }}>No issuers found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default IssuerTab;
